/**
 * Visões e escopo do trabalho (spec 018) — sem dependência de propósito: a
 * lista de tickets (./tickets) e o Hoje (./work) importam daqui, e um import
 * entre os dois fecharia um ciclo.
 *
 * Medido em `5c943de`: a lista de tickets forçava UMA mesa por vez e não tinha
 * filtro de responsável, SLA nem "meus", embora a API já aceitasse. As visões
 * abaixo são as perguntas que o atendente faz, na mesma ordem em que o Hoje as
 * mostra, e cada uma é um predicado ÚNICO no servidor (lista e indicadores
 * usam o mesmo — o número do indicador tem de bater com a lista que ele abre).
 */
import { z } from 'zod';

/**
 * - `mine`: Minha fila — abertos em que sou o responsável;
 * - `needs_reply`: o cliente falou por último em público (comentário do
 *   portal/e-mail ou WhatsApp vinculado) e ninguém da equipe respondeu;
 * - `at_risk`: SLA vencido ou a vencer (menos de 25% da janela) OU parado na
 *   etapa além do prazo dela (017, FR-008) — o risco do funil, não só do SLA;
 * - `unassigned`: abertos sem responsável;
 * - `waiting_client`: abertos numa etapa "Aguardando cliente/contato";
 * - `all`: sem recorte de trabalho (o filtro de status continua valendo).
 */
export const TICKET_LIST_VIEWS = [
  'mine',
  'needs_reply',
  'at_risk',
  'unassigned',
  'waiting_client',
  'all',
] as const;
export const TicketListViewSchema = z.enum(TICKET_LIST_VIEWS);
export type TicketListView = z.infer<typeof TicketListViewSchema>;

/**
 * Escopo do Hoje e da lista: `mine` = o que é meu OU está sem dono (o que eu
 * posso pegar); `team` = tudo o que eu enxergo. As regras de visibilidade
 * (mesas por grupo, `tickets.view_others`) valem nos dois — escopo nunca é
 * atalho para ver mais.
 */
export const WORK_SCOPES = ['mine', 'team'] as const;
export const WorkScopeSchema = z.enum(WORK_SCOPES);
export type WorkScope = z.infer<typeof WorkScopeSchema>;

/** Os quatro indicadores do Hoje — cada um é uma visão da lista. */
export const WORK_FOCUSES = ['needs_reply', 'at_risk', 'unassigned', 'waiting_client'] as const;
export const WorkFocusSchema = z.enum(WORK_FOCUSES);
export type WorkFocus = z.infer<typeof WorkFocusSchema>;

/** Situações de etapa que contam como "Aguardando cliente" (o cliente OU o contato). */
export const WAITING_CLIENT_SITUATIONS = ['waiting_client', 'waiting_contact'] as const;

/**
 * Autor de um comentário (spec 018): até aqui não se sabia quem escreveu — um
 * comentário sem `user_id` podia ser o solicitante respondendo por e-mail, a
 * automação ou o espelho do TiFlux, e "precisa de resposta" só existia no chat.
 *
 * `bot` (spec 022): a nota do bot — sempre interna, autor "Bot", escrita pela
 * API do bot com a chave do tenant. Não responde nem pergunta: para o "precisa
 * de resposta" ela é como `system`, e nunca vai ao TiFlux.
 */
export const COMMENT_AUTHOR_KINDS = ['staff', 'client', 'system', 'bot'] as const;
export const CommentAuthorKindSchema = z.enum(COMMENT_AUTHOR_KINDS);
export type CommentAuthorKind = z.infer<typeof CommentAuthorKindSchema>;
